import React, { useEffect, useState } from "react";
import { CardWithoutSeeMore, CardWithStatus } from "../../components/Cards";
import { SimpleBarChart } from "./InventoryCharts";
import PaginationComponentOne from "../../components/Pagination/Pagination-O";
import { useSearchParams, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSalesPerformancePerStore,
  fetchVariantSalesMetrics,
  fetchVariantSummary,
  getActivityLogs,
  getAllStockRequests,
} from "../../redux/Inventory/inventoryActions";
import EmptyState from "../../components/EmptyState";
import moment from "moment";
import toast from "react-hot-toast";
import { ContentCopy } from "@mui/icons-material";


const getActivityTypeBadgeStyle = (activityType) => {
  const styles = {
    STOCK_IN: "border-[#2B8C34] text-[#2B8C34]",
    STOCK_OUT: "border-[#B92020] text-[#B92020]",
    STOCK_TRANSFER: "border-[#0066CC] text-[#0066CC]",
    STOCK_ADJUSTMENT: "border-[#6B46C1] text-[#6B46C1]",
    OUT_OF_STOCK: "border-[#B92020] text-[#B92020]",
    LOW_STOCK: "border-[#D4A407] text-[#D4A407]",
    BACK_IN_STOCK: "border-[#2B8C34] text-[#2B8C34]",
    NEW_PRODUCT: "border-[#6B46C1] text-[#6B46C1]",
    NEW_VARIANT: "border-[#6B46C1] text-[#6B46C1]",
    STOCK_REQUEST: "border-[#D4A407] text-[#D4A407]",
    STOCK_CONFIRMATION: "border-[#2B8C34] text-[#2B8C34]",
  };
  return styles[activityType] || "border-[#5F6D60] text-[#5F6D60]";
};

const OverviewTab = ({ overviewRefetch }) => {
  const dispatch = useDispatch();
  const {
    variantSummary,
    variantSalesMetrics,
    salesPerfomanceByStore,
    loading,
    salesMetricsBreakdown,
    salesPerformanceTotalCount,
    activityLogs,
    activityLogsLoading,
    activityLogsTotalCount,
    // stockRequestsData,
    stockRequestsTotalCount,
    stockRequestsTabCounts,
  } = useSelector((state) => state.inventory);

  const [searchParams] = useSearchParams();
  const [timeframe, setTimeframe] = useState("12 months");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [activityPage, setActivityPage] = useState(1);
  const [activityPageSize] = useState(10);
  const variant = searchParams.get("variant");
  const { id: productId } = useParams();

  const handlePageChange = (paginationData) => {
    // Carbon's Pagination component passes an object with page and pageSize
    if (typeof paginationData === "object") {
      if (paginationData.page !== undefined) {
        setPage(paginationData.page);
      }
      if (
        paginationData.pageSize !== undefined &&
        paginationData.pageSize !== pageSize
      ) {
        setPageSize(paginationData.pageSize);
        setPage(1); // Reset to first page when page size changes
      }
    } else {
      setPage(paginationData);
    }
  };

  // Period mapping function
  const getPeriodFromTimeframe = (timeframe) => {
    const mapping = {
      "12 months": "12_months",
      "3 months": "3_months",
      "7 days": "7_days",
    };
    return mapping[timeframe] || "12_months";
  };

  useEffect(() => {
    if (overviewRefetch && variant) {
      // Refetch variant summary
      dispatch(
        fetchVariantSummary({
          variantId: variant,
        })
      );

      // Refetch stock requests count
      dispatch(
        getAllStockRequests({
          page: 1,
          page_size: 1,
          search: "",
          status: "",
          variantId: variant,
        })
      );
    }
  }, [dispatch, variant, overviewRefetch]);

  useEffect(() => {
    // Only dispatch if variant is available
    if (variant) {
      dispatch(
        fetchVariantSummary({
          variantId: variant,
        })
      );
    }
  }, [dispatch, variant]);

  useEffect(() => {
    if (variant) {
      const period = getPeriodFromTimeframe(timeframe);

      // Dispatch both API calls with the selected period
      dispatch(
        fetchVariantSalesMetrics({
          variantId: variant,
          period: period,
        })
      );

      dispatch(
        fetchSalesPerformancePerStore({
          variantId: variant,
          period: period,
          page: page,
          page_size: pageSize,
        })
      );

      // Fetch ALL stock requests (without status filter) to get total count
      dispatch(
        getAllStockRequests({
          page: 1,
          page_size: 1, // We only need the count, so minimal page size
          search: "",
          status: "", // No status filter to get all requests
          variantId: variant,
        })
      );
    }
  }, [dispatch, variant, timeframe, page, pageSize]);

  // Fetch activity logs for this specific variant
  useEffect(() => {
    if (variant) {
      dispatch(
        getActivityLogs({
          product_id: variant,
          product_type: "productvariant",
          page: activityPage,
          page_size: activityPageSize,
        })
      );
    }
  }, [dispatch, variant, activityPage, activityPageSize]);

  const handleCopyNumber = (number) => {
    navigator.clipboard.writeText(number);
    toast.success(`${number} copied successfully`);
  };

  // Calculate total stock requests from tab counts (more reliable)
  const totalStockRequests = stockRequestsTabCounts
    ? Object.values(stockRequestsTabCounts).reduce(
        (sum, count) => sum + count,
        0
      )
    : stockRequestsTotalCount || 0;

  // Determine if there are urgent requests (pending or low stock)
  const hasUrgentRequests =
    (stockRequestsTabCounts?.PENDING || 0) > 0 ||
    (variantSummary?.stock_requests?.low_stock_stores_count || 0) > 0;

  return (
    <>
      {/* Top Overview Cards */}
      <div className="grid grid-cols-2 gap-4 mt-4">
        <CardWithoutSeeMore
          title="Stock Value"
          subtitle={`₦${
            variantSummary?.stock_summary?.total_stock_value?.toLocaleString() ||
            "0"
          }`}
          href=""
          hasStatus
        />
        <CardWithoutSeeMore
          title="Stock"
          subtitle={`${variantSummary?.stock_summary?.total_stock || "0"}`}
          href=""
          hasStatus
        />
      </div>

      <div className="grid grid-cols-3 gap-4 mt-4">
        <CardWithoutSeeMore
          title="Selling Price Range"
          subtitle={`₦${
            variantSummary?.price_range?.min_selling_price?.toLocaleString() ||
            "0"
          } - ₦${
            variantSummary?.price_range?.max_selling_price?.toLocaleString() ||
            "0"
          }`}
          href=""
          hasStatus
        />
        <CardWithStatus
          title="Stock Request"
          subtitle={totalStockRequests} // ← Use calculated total instead of array length
          href={`/product-inventory/${productId}/stock-requests?variant=${variant}`}
          hasStatus
          status={hasUrgentRequests ? "Urgent" : "Normal"} // ← Dynamic status based on pending requests
        />
        <CardWithStatus
          title="Low Stock"
          subtitle={variantSummary?.stock_requests?.low_stock_stores_count || 0}
          href={`/product-inventory/${productId}?variant=${variant}`}
          hasStatus
          status="Urgent"
        />
      </div>

      {/* Quantity Sold + Revenue + Chart + Table + Activity Log */}
      <div className="mt-4 grid grid-cols-3 gap-4">
        <div className="col-span-3 rounded-lg border border-[#E1E6E1] p-4 min-h-[360px] bg-[#FFFF]">
          {/* Quantity Sold and Revenue */}
          <div className="flex justify-between mb-8">
            <div className="flex items-center gap-5">
              <div className="flex flex-col">
                <p className="font-semibold text-sm text-[#5F6D60]">
                  Quantity Sold
                </p>
                <p className="font-semibold text-3xl text-[#344335] mt-3">
                  {variantSalesMetrics?.total_metrics?.total_quantity_sold.toLocaleString() ||
                    0}
                </p>
              </div>

              <div className="flex flex-col">
                <p className="font-semibold text-sm text-[#5F6D60]">Revenue</p>
                <p className="font-semibold text-3xl text-[#344335] mt-3">
                  ₦
                  {variantSalesMetrics?.total_metrics?.total_revenue.toLocaleString() ||
                    0}
                </p>
              </div>
            </div>

            <div className="mb-4">
              <select
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
                className="p-2 text-sm font-semibold text-[#5F6D60] focus:outline-none focus:ring-0"
              >
                <option value="12 months">12 Months</option>
                <option value="3 months">3 Months</option>
                <option value="7 days">7 Days</option>
              </select>
            </div>
          </div>

          {/* Bar chart */}
          <SimpleBarChart data={salesMetricsBreakdown} />

          {/* Table */}
          <div className="overflow-x-auto relative mt-3 mb-3 p-4 col-span-3 rounded-lg border border-[#E1E6E1] bg-[#FFFF]">
            {loading ? (
              <div className="animate-pulse w-full mb-4">
                <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
                <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
                <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
                <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
                <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
                <div className="h-10 bg-gray-200 rounded-lg  w-full"></div>
              </div>
            ) : !salesPerfomanceByStore ||
              salesPerfomanceByStore.length === 0 ? (
              <div className="">
                <EmptyState text="No sales performance data available for this period." />
              </div>
            ) : (
              <>
                <table className="w-full text-left rounded-lg overflow-hidden border border-[#E1E6E1]">
                  <thead className="bg-[#F6F7F6] border-[#E1E6E1]">
                    <tr className="text-sm">
                      <th className="px-4 py-4 font-semibold text-[#5F6D60] rounded-tl-lg rounded-bl-lg">
                        Store
                      </th>
                      <th className="px-1 py-4 font-semibold text-[#5F6D60]">
                        Quantity Sold
                      </th>
                      <th className="px-1 py-4 font-semibold text-[#5F6D60]">
                        Selling Price
                      </th>
                      <th className="px-1 py-4 font-semibold text-[#5F6D60]">
                        Revenue
                      </th>
                      <th className="px-1 py-4 font-semibold text-[#5F6D60] rounded-tr-lg rounded-br-lg">
                        Stock
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white border-[#E1E6E1]">
                    {salesPerfomanceByStore.map((store) => (
                      <tr
                        key={store.location_id}
                        className="text-sm hover:bg-[#EDF7EE] border-b border-[#E1E6E1]"
                      >
                        <td className="px-4 py-4 text-[#5F6D60]">
                          {store.location_name}
                        </td>
                        <td className="px-1 py-4 text-[#5F6D60]">
                          <p className="border w-fit py-1 px-2 rounded-md font-medium">
                            {store.quantity_sold}
                          </p>
                        </td>
                        <td className="px-1 py-4 text-[#5F6D60]">
                          <p className="border w-fit py-1 px-2 rounded-md">
                            ₦{store.unit_cost?.toLocaleString() || 0}
                          </p>
                        </td>
                        <td className="px-1 py-4 text-[#5F6D60]">
                          <p className="border w-fit py-1 px-2 rounded-md">
                            ₦{store.revenue?.toLocaleString() || 0}
                          </p>
                        </td>
                        <td className="px-1 py-4 text-[#5F6D60]">
                          <p
                            className={`border w-fit py-1 px-2 rounded-md font-medium ${
                              store.current_stock === 0
                                ? "border-[#B92020] text-[#B92020]"
                                : store.current_stock < 20
                                ? "border-[#D4A407] text-[#D4A407]"
                                : "border-[#2B8C34] text-[#2B8C34]"
                            }`}
                          >
                            {store.current_stock}
                          </p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* Pagination */}
            {salesPerfomanceByStore && salesPerfomanceByStore.length > 0 && (
              <div className="border-t border-[#E1E6E1]">
                <PaginationComponentOne
                  totalItems={salesPerformanceTotalCount || 0}
                  page={page}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>

        {/* Activity Log */}
        <div className="col-span-3 bg-white border border-[#E1E6E1] rounded-lg h-fit mt-4 flex flex-col">
          <div className="flex border-b border-[#E1E6E1] justify-between items-center px-4 py-3">
            <p className="text-sm text-[#2E332F] font-semibold">Activity Log</p>
          </div>
          <div className="overflow-y-auto max-h-[300px] flex-1">
            {activityLogsLoading ? (
              <div className="animate-pulse w-full py-4">
                <div className="h-12 bg-gray-200 rounded-lg w-full mb-4"></div>
                <div className="h-12 bg-gray-200 rounded-lg w-full mb-4"></div>
                <div className="h-12 bg-gray-200 rounded-lg w-full mb-4"></div>
                <div className="h-12 bg-gray-200 rounded-lg w-full"></div>
              </div>
            ) : !activityLogs || activityLogs.length === 0 ? (
              <div className="py-8">
                <EmptyState text="No recent activities" />
              </div>
            ) : (
              activityLogs.map((activity, index) => (
                <div
                  key={activity.id}
                  className={` ${
                    index < activityLogs.length - 1
                      ? "border-b border-[#E1E6E1] "
                      : ""
                  }`}
                >
                  <div className="flex justify-between items-center  py-2 px-6 bg-[#F6F7F6] ">
                    <span
                      className={`px-2 py-1 rounded border ${getActivityTypeBadgeStyle(
                        activity.activity_type
                      )} bg-white font-semibold text-xs`}
                    >
                      {activity?.activity_type_display ||
                        activity.activity_type}
                    </span>
                    <span className="text-xs text-[#5F6D60] font-semibold">
                      {moment(activity.timestamp).format(
                        "MMM DD, YYYY hh:mm A"
                      )}
                    </span>
                  </div>
                  {/* Product Name */}
                  <div className=" px-6 py-2 border-t border-[#E1E6E1] flex items-center">
                    <p className="text-sm text-[#5F6D60] font-semibold">
                      Product Name:
                    </p>
                    <div>
                      <span className="text-sm text-[#5F6D60] border rounded-sm py-0.5 px-1 font-semibold ml-2">
                        {activity?.details?.product_name || "N/A"}
                      </span>{" "}
                      /
                      <span className="text-sm text-[#5F6D60] border rounded-sm py-0.5 px-1 font-semibold ml-2">
                        {activity?.details.variant_data?.type || "N/A"}
                      </span>{" "}
                      /
                      <span className="text-sm text-[#5F6D60] border rounded-sm py-0.5 px-1 font-semibold ml-2">
                        {activity?.details?.variant_data?.value || "N/A"}

                        {activity?.details.variant_data?.unit || "N/A"}
                      </span>
                    </div>
                  </div>
                  {/* Unit Added */}
                  {activity?.details.quantity > 0 && (
                    <div className="px-6 py-2 border-t border-[#E1E6E1] flex justify-between items-center">
                      <div className="flex items-center ">
                        <p className="text-sm text-[#5F6D60] font-semibold">
                          Units Added:
                        </p>
                        <span
                          className={`text-xs font-semibold px-2 py-1 rounded border ml-2 ${
                            activity.activity_type === "STOCK_IN" ||
                            activity.activity_type === "STOCK_TRANSFER-INCOMING"
                              ? "text-[#2B8C34] border-[#2B8C34] bg-[#EDF7EE]"
                              : "text-[#5F6D60] border-[#E1E6E1] bg-[#FFFF]"
                          }`}
                        >
                          {activity.activity_type === "STOCK_IN" ||
                          activity.activity_type === "STOCK_TRANSFER-INCOMING"
                            ? "+"
                            : "-"}
                          {activity.details.quantity}
                        </span>
                      </div>
                      {/* Stock Id */}
                      <div className="border border-[#E1E6E1] px-1 py-1 rounded bg-white w-fit gap-0.5 flex items-center text-xs text-[#5F6D60] font-semibold">
                        {activity?.details?.reference || "---"}
                        <ContentCopy
                          className="text-[#5F6D60] cursor-pointer"
                          style={{ fontSize: "10px" }}
                          onClick={() =>
                            handleCopyNumber(activity?.details?.reference)
                          }
                        />
                      </div>
                    </div>
                  )}
                  {/* Store */}
                  <div className="px-6 py-2 border-t border-[#E1E6E1] flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-[#5F6D60] font-semibold">
                        Store:
                      </p>
                      <span className="text-sm text-[#5F6D60] font-semibold  border border-[#E1E6E1] rounded-md py-0.5 px-1 bg-[#F6F7F6]">
                        {activity?.details?.location || "N/A"}
                      </span>
                    </div>
                    <button className="text-sm text-[#2B8C34] font-semibold">
                      See Stock Details
                    </button>
                  </div>

                  {/* Stock added by */}
                  {activity?.actor && (
                    <div className="flex items-center border-t border-[#E1E6E1] px-6 py-2">
                      <span className="text-sm font-semibold text-[#5F6D60] mr-2">
                        {activity?.activity_type === "STOCK_IN"
                          ? "Stock Added By:"
                          : activity?.activity_type.includes("TRANSFER")
                          ? "Stock Transferred By:"
                          : "Sold By:"}
                      </span>
                      <span className="text-xs font-semibold text-[#2E332F]">
                        {activity?.actor.name || activity?.actor.username}
                      </span>
                      {activity.actor.role && (
                        <span className="text-xs text-[#5F6D60] ml-2 bg-[#F6F7F6] font-semibold border border-[#E1E6E1] px-2 py-1 rounded">
                          {activity.actor.role}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Activity Log Pagination*/}
          {activityLogs && activityLogs.length > 0 && (
            <div className="border-t border-[#E1E6E1] px-4 flex-shrink-0">
              <PaginationComponentOne
                totalItems={activityLogsTotalCount || 0}
                page={activityPage}
                pageSize={activityPageSize}
                onPageChange={(paginationData) => {
                  if (typeof paginationData === "object") {
                    if (paginationData.page !== undefined) {
                      setActivityPage(paginationData.page);
                    }
                  } else {
                    setActivityPage(paginationData);
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OverviewTab;
